type RepresentativeWarningParams = { name: string; role: string; clientName: string };

// Local translations
export const T = {
  en_GB: {
    copy: "Copy",
    inviteTitle: (name: string) => `Invite ${name} to Complete Flow`,
    inviteDescription: "Send a secure invitation to the client via email or SMS",
    inviteDescriptionMembers:
      "Generate the group's access link, then send each member their own link below.",
    inviteDescriptionRepresentatives:
      "Generate the access link, then send each representative their own link below.",
    groupLinkNotForSending:
      "This group link is not for sending — members sign in with their own links below.",
    clientLinkNotForSending:
      "This link is not for sending — each representative signs in with their own link below.",
    step1Title: "Step 1: Generate Access Link",
    step1Description: "Create a secure link for the client to access this flow",
    generating: "Generating...",
    generateLink: "Generate Link",
    publicAccessLink: "Public Access Link",
    revokeAccess: "Revoke Access",
    emailInvite: "Email Invite",
    smsInvite: "SMS Invite",
    clientEmail: "Client Email",
    emailUpdatedNote: 'Email has been updated. Click "Save" to confirm.',
    addEmailNote: "Add client email to send email invite",
    mobilePhone: "Mobile Phone",
    phoneUpdatedNote: 'Phone number has been updated. Click "Save" to confirm.',
    addPhoneNote: "Add client phone number to send SMS invite",
    cancel: "Cancel",
    save: "Save",
    sending: "Sending...",
    sendEmailInvite: "Send Email Invite",
    sendSmsInvite: "Send SMS Invite",
    failedToGenerateLink: "Failed to generate access link",
    accessLinkGenerated: "Access link generated!",
    failedToUpdateClient: "Failed to update client information",
    clientInfoUpdated: "Saved",
    generateLinkFirst: "Please generate an access link first",
    enterEmailAddress: "Please enter an email address",
    failedToSendEmail: "Failed to send email",
    inviteSentTo: (recipient: string) => `Invite sent to ${recipient}!`,
    enterPhoneNumber: "Please enter a mobile phone number",
    failedToSendSms: "Failed to send SMS",
    linkCopied: "Link copied to clipboard!",
    revokePublicAccessTitle: "Revoke Public Access",
    revokePublicAccessDescription:
      "Are you sure you want to revoke public access? The existing link will no longer work.",
    revokeAccessConfirm: "Revoke Access",
    failedToRevokeAccess: "Failed to revoke access",
    accessRevokedSuccess: "Public access revoked successfully!",
    linkNoLongerActive: (reason: string) =>
      `This link no longer works (${reason}). Generate a new one to invite again.`,
    revokeReasonLabels: {
      revoked: "revoked by an advisor",
      representative_removed: "the representative was removed",
      representative_changed: "the representative changed",
      expired: "it expired",
    } as Record<string, string>,
    qrModalTitle: "Public Access Link",
    representativeWarning: (p: RepresentativeWarningParams) =>
      `This link is for ${p.name} (${p.role}) only. ${p.clientName} cannot use it. ${p.name} signs in with a one-time code sent to their own e-mail or phone, or with BankID.`,
    roleLabels: {
      poa: "power of attorney",
      guardian: "guardian",
      representative: "representative",
      group_member: "the group's representative",
    } as Record<string, string>,
    representativeContactHintParties: (name: string) =>
      `To change ${name}'s contact details, edit the representative on the client's Representative tab.`,
    representativeContactHintMembers: (name: string) =>
      `To change ${name}'s contact details, edit the representative on the client's Members tab.`,
    openClientPage: "Open client page",
    representativeCannotSignIn: (name: string) =>
      `${name} has no e-mail, phone or national ID on record, so they cannot sign in with this link. Add their contact details or national ID first.`,
    representativeBankIdOnly: (name: string) =>
      `${name} has no e-mail or phone on record, so the link can't be sent to them and they can only sign in with BankID. Share the link another way, or add their contact details.`,
  },
  nb_NO: {
    copy: "Kopier",
    inviteTitle: (name: string) => `Inviter ${name} til å fullføre flyten`,
    inviteDescription: "Send en sikker invitasjon til kunden via e-post eller SMS",
    groupLinkNotForSending:
      "Gruppelenken skal ikke sendes — medlemmene logger inn med sine egne lenker nedenfor.",
    clientLinkNotForSending:
      "Denne lenken skal ikke sendes — hver representant logger inn med sin egen lenke nedenfor.",
    inviteDescriptionMembers:
      "Generer gruppens tilgangslenke, og send deretter hvert medlem sin egen lenke nedenfor.",
    inviteDescriptionRepresentatives:
      "Generer tilgangslenken, og send deretter hver representant sin egen lenke nedenfor.",
    step1Title: "Steg 1: Generer tilgangslenke",
    step1Description: "Opprett en sikker lenke for kunden til å få tilgang til denne flyten",
    generating: "Genererer...",
    generateLink: "Generer lenke",
    publicAccessLink: "Offentlig tilgangslenke",
    revokeAccess: "Tilbakekall tilgang",
    emailInvite: "E-postinvitasjon",
    smsInvite: "SMS-invitasjon",
    clientEmail: "Kundens e-post",
    emailUpdatedNote: 'E-post har blitt oppdatert. Klikk "Lagre" for å bekrefte.',
    addEmailNote: "Legg til kundens e-post for å sende e-postinvitasjon",
    mobilePhone: "Mobiltelefon",
    phoneUpdatedNote: 'Telefonnummer har blitt oppdatert. Klikk "Lagre" for å bekrefte.',
    addPhoneNote: "Legg til kundens telefonnummer for å sende SMS-invitasjon",
    cancel: "Avbryt",
    save: "Lagre",
    sending: "Sender...",
    sendEmailInvite: "Send e-postinvitasjon",
    sendSmsInvite: "Send SMS-invitasjon",
    failedToGenerateLink: "Kunne ikke generere tilgangslenke",
    accessLinkGenerated: "Tilgangslenke generert!",
    failedToUpdateClient: "Kunne ikke oppdatere kundeinformasjon",
    clientInfoUpdated: "Lagret",
    generateLinkFirst: "Vennligst generer en tilgangslenke først",
    enterEmailAddress: "Vennligst skriv inn en e-postadresse",
    failedToSendEmail: "Kunne ikke sende e-post",
    inviteSentTo: (recipient: string) => `Invitasjon sendt til ${recipient}!`,
    enterPhoneNumber: "Vennligst skriv inn et mobilnummer",
    failedToSendSms: "Kunne ikke sende SMS",
    linkCopied: "Lenke kopiert til utklippstavlen!",
    revokePublicAccessTitle: "Tilbakekall offentlig tilgang",
    revokePublicAccessDescription:
      "Er du sikker på at du vil tilbakekalle offentlig tilgang? Den eksisterende lenken vil ikke lenger fungere.",
    revokeAccessConfirm: "Tilbakekall tilgang",
    failedToRevokeAccess: "Kunne ikke tilbakekalle tilgang",
    accessRevokedSuccess: "Offentlig tilgang tilbakekalt!",
    linkNoLongerActive: (reason: string) =>
      `Denne lenken virker ikke lenger (${reason}). Generer en ny for å invitere igjen.`,
    revokeReasonLabels: {
      revoked: "tilbakekalt av en rådgiver",
      representative_removed: "representanten ble fjernet",
      representative_changed: "representanten endret seg",
      expired: "den har utløpt",
    } as Record<string, string>,
    qrModalTitle: "Offentlig tilgangslenke",
    representativeWarning: (p: RepresentativeWarningParams) =>
      `Denne lenken er kun for ${p.name} (${p.role}). ${p.clientName} kan ikke bruke den. ${p.name} logger inn med en engangskode sendt til egen e-post eller telefon, eller med BankID.`,
    roleLabels: {
      poa: "fullmektig",
      guardian: "verge",
      representative: "representant",
      group_member: "gruppens representant",
    } as Record<string, string>,
    representativeContactHintParties: (name: string) =>
      `For å endre ${name}s kontaktopplysninger, rediger representanten under kundens Representant-fane.`,
    representativeContactHintMembers: (name: string) =>
      `For å endre ${name}s kontaktopplysninger, rediger representanten under kundens Medlemmer-fane.`,
    openClientPage: "Åpne kundesiden",
    representativeCannotSignIn: (name: string) =>
      `${name} har verken e-post, telefon eller fødselsnummer registrert, og kan derfor ikke logge inn med denne lenken. Legg inn kontaktopplysninger eller fødselsnummer først.`,
    representativeBankIdOnly: (name: string) =>
      `${name} har verken e-post eller telefon registrert, så lenken kan ikke sendes til dem, og de kan bare logge inn med BankID. Del lenken på annen måte, eller legg inn kontaktopplysninger.`,
  },
} as const;
